import "../static/css/style.css";
import "../static/css/ui.css";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import Stats from "Stats.js";

import Base from "./models/base";
import { Table } from "./models/model_table.js";
import { Chair } from "./models/model_chair.js";
import { LinearDimension } from "./library/LinearDimension";

class Simulator extends Base {
  constructor(config = {}) {
    super();

    this._init(config);
    this._setupCamera();
    this._setupLight();
    this._setupRenderer();
    this._setupControls();
    this._setupModel();
    this._setupTool();

    window.onresize = this._resize.bind(this);
    // window.onclick = this.click.bind(this);

    this.on('resize', () => {
      this._resize();
    })

    const isTouchDevice = "ontouchstart" in document.documentElement;
    let startPt = null;
    // let drag = false;

    document.addEventListener(
      isTouchDevice ? "touchstart" : "mousedown",
      (e) => {
        // drag = false;
        startPt = this._getCurrentMousePosition(e);
      }
    );

    // document.addEventListener(isTouchDevice ? 'touchmove' : 'mousemove', () => drag = true);
    document.addEventListener(isTouchDevice ? "touchend" : "mouseup", (e) => {
      const endPt = this._getCurrentMousePosition(e);
    //   console.log(startPt, endPt);

      if (
        Math.abs(startPt.x - endPt.x) <= 5 &&
        Math.abs(startPt.y - endPt.y) <= 5
      ) {
        this._click(e);
      }
    });

    this._resize();

    requestAnimationFrame(this._render.bind(this));

    this.showInitMessage();
    
  }

  showInitMessage() {
    this.showMessage("우측에서 제품을 선택해주세요");
    this._mode = "init";
  }

  hideInitMessage() {
    this.hideMessage();
  }

  showMessage(message) {
    this._message.classList.remove("hide");
    this._message.innerHTML = message;
  }

  hideMessage() {
    this._message.classList.add("hide");
    this._message.innerHTML = "";
    this._mode = "view";
    this._edit_model = null;
  }

  loadHDRI(url) {
    return new Promise((resolve) => {
      const loader = new RGBELoader();
      const pmremGenerator = new THREE.PMREMGenerator(this._renderer);
      loader.load(url, (texture) => {
        const envMap = pmremGenerator.fromEquirectangular(texture).texture;
        texture.dispose();
        pmremGenerator.dispose();

        resolve(envMap);
      });
    });
  }

  getMode() {
    return this._mode;
  }

  _init(config) {
    this._config = {
      viewMode: false,
      viewStats: false,
      wrapper: ".simulator",
      envImage: "./textures/neutral.hdr",
      light: {
        distance: 0,
        depth: 1.3
      },
      view: {
        helper: false,
        dimension: false,
        dimensionPosition: {
          zoom: 1.5,
          z: 1.5,
          x: 2.44,
          y: 2.54,
          z: 3.78
        },
        zoomPosition: {
          zoom: 1.5,
          z: 1.5,
          x: 2.44,
          y: 2.54,
          z: 3.78
        }
      },
    };

    this._config = Object.assign(this._config, config);

    this._mode = "init";

    this._wrapper = document.querySelector(this._config.wrapper);
    this._canvas = this._wrapper.querySelector("canvas");
    this._message = this._wrapper.querySelector(".simulator-message");

    this._clock = new THREE.Clock();

    const scene = new THREE.Scene();
    this._scene = scene;

    this._objects = [];

    this._popup = null;
    this._current_model = null;

    this._raycaster = new THREE.Raycaster();

    this._manager = new THREE.LoadingManager();

    this._manager.onStart = ( url, itemsLoaded, itemsTotal ) => {
      this._onModelLoadingStart();
    };

    this._manager.onLoad = ( ) => {
      this._onModelLoadingEnd();
    };
    
    this._manager.onProgress = ( url, itemsLoaded, itemsTotal ) => {
    //   // console.log( 'Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.' );
    //   console.log((itemsTotal - itemsLoaded));
      this._onModelLoadingProgress(itemsTotal - itemsLoaded);
    };
    
    this._manager.onError = ( url ) => {
      console.log( 'There was an error loading ' + url );
    };

    if(this._config.viewStats) {
      this._stats = new Stats();
      this._stats.showPanel( 0 ); // 0: fps, 1: ms, 2: mb, 3+: custom
      document.body.appendChild( this._stats.dom )
    }
  }

  _setupControls() {
    this._controls = new OrbitControls(this._camera, this._canvas);
    this._controls.enableDamping = true;
    this._controls.enablePan = false;
    this._controls.minDistance = 5;
    this._controls.maxDistance = 10;
    this._controls.minPolarAngle = 0; // radians
    this._controls.maxPolarAngle = Math.PI / 2; // radians
    this._controls.rotateSpeed = 0.8;
    this._controls.dampingFactor = 0.8;

    this._controls.addEventListener("change", this._onCameraChange.bind(this));
    this._controls.addEventListener(
      "start",
      this._onCameraChangeStart.bind(this)
    );

    if(this._config.view.helper) {
      const gridHelper = new THREE.GridHelper(10, 100)
      this._scene.add(gridHelper)
    }
  }

  _setupCamera() {
    const parentSize = this._wrapper.getBoundingClientRect();
    const camera = new THREE.PerspectiveCamera(
      20,
      parentSize.width / parentSize.height,
      0.1,
      100
    );

    camera.position.z = 10;
    camera.position.x = 6;
    camera.position.y = 10;
    this._camera = camera;
  }

  _setupLight() {
    // const ambientLight = new THREE.HemisphereLight(0xffffff, 0x999999, 0.8) // sky, ground,
    const ambientLight = new THREE.HemisphereLight(0xffffff, 0xaaaaaa, 0.6); // sky, ground,
    this._scene.add(ambientLight);

    // const light = new THREE.SpotLight(0xffffff,0.05)
    // light.position.set(-1,2,1);
    // // light.position.multiplyScalar(2);
    // // light.castShadow = true;
    // // light.shadow.radius = 8;
    // this._scene.add( light );

    // const lightHelper = new THREE.SpotLightHelper(light, 3);
    // this._scene.add(lightHelper);

    // const light2 = new THREE.PointLight(0xffffff, 1);
    // // light2.castShadow = true;
    // // light2.position.set(200,200,200);
    // // light2.shadow.radius = 8;
    // this._scene.add( light2 );

    const mainLight = new THREE.DirectionalLight(0xffffff, 0.4);
    mainLight.position.set(
      -this._config.light.distance,
      this._config.light.depth,
      this._config.light.distance
    );

    mainLight.castShadow = 1;
    // mainLight.shadow.camera.far = 15
    mainLight.shadow.mapSize.width = 1024 * 4;
    mainLight.shadow.mapSize.height = 1024 * 4;
    mainLight.shadow.radius = 3;
    mainLight.shadow.bias = -0.00011;

    // mainLight.castShadow = true;
    // mainLight.shadow.camera.top = 4;
    // mainLight.shadow.camera.bottom = - 4;
    // mainLight.shadow.camera.left = - 4;
    // mainLight.shadow.camera.right = 4;
    // mainLight.shadow.camera.near = 0.1;
    // mainLight.shadow.camera.far = 40;
    // mainLight.shadow.camera.far = 40;
    // mainLight.shadow.bias = - 0.002;
    // mainLight.shadow.radius = 3;
    // mainLight.position.set( 0, 4, 4 );

    this._scene.add(mainLight);
    this._scene.add(mainLight.target);

    const subLight = new THREE.DirectionalLight(0xffffff, 0.4);
    subLight.shadow.camera.far = 15
    subLight.shadow.mapSize.width = 1024 * 4;
    subLight.shadow.mapSize.height = 1024 * 4;
    subLight.shadow.radius = 3;
    subLight.shadow.bias = -0.00011;
    subLight.position.set( 0, 2, 2 );
    this._scene.add(subLight);
    this._scene.add(subLight.target);

    // const subLightHelper = new THREE.DirectionalLightHelper(subLight, 5, 0xff0000)
    // this._scene.add(subLightHelper)

    const subLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    subLight2.shadow.camera.far = 15
    subLight2.shadow.mapSize.width = 1024 * 4;
    subLight2.shadow.mapSize.height = 1024 * 4;
    subLight2.shadow.radius = 3;
    subLight2.shadow.bias = -0.00011;
    subLight2.position.set( 0, 2, -2 );
    this._scene.add(subLight2);
    this._scene.add(subLight2.target);

    // const subLight2Helper = new THREE.DirectionalLightHelper(subLight2, 5, 0xff0000)
    // this._scene.add(subLight2Helper)

    if(this._config.view.helper) {
      const mainLightHelper = new THREE.DirectionalLightHelper(mainLight, 5, 0xff0000)
      this._scene.add(mainLightHelper)
    }
  }

  async _setupRenderer() {
    const parentSize = this._wrapper.getBoundingClientRect();

    this._sizes = {
      width: parentSize.width,
      height: parentSize.height,
    };

    const renderer = new THREE.WebGLRenderer({
      canvas: this._canvas,
      alpha: true,
      antialias: true,
      autoClear: true,
      preserveDrawingBuffer: true,
      logarithmicDepthBuffer: true,
    });

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    // renderer.toneMappingExposure = 0.7
    // renderer.physicallyCorrectLights = false;
    renderer.setClearColor(0xffffff, 1);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.render(this._scene, this._camera);
    this._renderer = renderer;

    this._renderer.setSize(this._sizes.width, this._sizes.height);

    const envMap = await this.loadHDRI(this._config.envImage);
    envMap.encoding = THREE.sRGBEncoding;
    this._scene._environment = envMap;
  }

  _setupModel() {
    const floorGeometry = new THREE.PlaneGeometry(2e4, 2e4);
    floorGeometry.rotateX(-Math.PI / 2);
    
    // const floorMaterial = new THREE.MeshStandardMaterial({
    //   color: 0xeeeeee,
    //   // side: THREE.DoubleSide,
    // });

    // this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    // this._floor.receiveShadow = true;
    // // this._floor.material.emissive.setHex(12303291);
    // // this._floor.material.emissiveIntensity = 0.45;
    // // this._floor.position.y = -0.03
    // this._scene.add(this._floor);

    var planeMaterial = new THREE.ShadowMaterial({
      color: 0xaaaaaa,
      opacity: 0.2
    });
    var shadowPlane = new THREE.Mesh( floorGeometry, planeMaterial );
    shadowPlane.receiveShadow = true;
    this._scene.add(shadowPlane);
  }

  _setupTool() {
    this._linear_dimension = new LinearDimension(
      this._wrapper,
      this._renderer,
      this._camera
    );
    if (!this._config.view.dimension) {
      this._linear_dimension.hide();
    }
  }

  _onCameraChange() {
    // const cameraState = JSON.stringify(this._camera.matrix.toArray());
    // console.log(cameraState);

    if (this._linear_dimension && this._config.view.dimension) {
      this._linear_dimension.update(this._camera);
    }
  }

  _onCameraChangeStart() {
    this._resetViewSetting();
  }

  _reset() {
    this._linear_dimension.setTarget(null);

    if (this._table) {
      this._table = null;
    }

    if (this._current_model) {
      this._current_model = null;
    }
  }

  _resetViewSetting() {
    this.hidePopup();

    // 카메라 이동 시 수치 모드 종료
    if (this._config.view.dimension) {
      this._update("viewDimension", false);
    }
  }

  // 현재 보여지는 모델이 있는지에 따라 기본 메시지 여부
  _check_page() {
    if (this._current_model) {
      if (this._mode !== "view") {
        this.hideInitMessage();
        this._onViewMode()
      }
    } else {
      if (this._mode !== "init") {
        this.showInitMessage();
        this._onInitMode()
      }
    }
  }

  // From Child Event

  _onModelEditByPopup(model) { // 모델을 선택 후 편집하기 클릭
    if(model) {
      if (model) {
        this._mode = "edit";
        this._edit_model = model;

        this.showMessage("<div class='simulator-message-content'>변경할 모델을 오른쪽에서 클릭하세요.<br /><a href='#' class='btn-simulator-edit-cancel'>수정취소</a></div>");
        this._message.querySelector('.btn-simulator-edit-cancel').onclick = () => {
          this.hideInitMessage();
          this._onViewMode();
          return false;
        }

        this._onEditMode(model);
      }
    }
  }

  _onModelDeleteByPopup(model) { // 모델을 선택 후 삭제하기 클릭
    if(model) {
      this.trigger('onRemoveModel', model);

      if (model === this._current_model) {
        this._current_model = null;
      }

      if (model === this._table) {
        this._table = null;
      }

      this._check_page();
    }
  }
  
  _onModelAddCancel(model) { // 모델 추가 취소됨
    this.trigger('onAddModelCancel', model);
  }

  _onModelLoadSingle(model) { // 모델 로딩 완료
    this.trigger('onLoadModelSingle', model);
  }

  _onModelLoadSinglePrgress(model, percent) {
    this.trigger('onLoadModelSinglePrgress', {model, percent});
  }

  _onModelChangeBefore(oldModelInfo) { // 모델 변경 전
    this.trigger('onChangeModelBefore', oldModelInfo);
  }

  _onModelChange(oldModelInfo, model) { // 모델 변경 및 로딩 완료
    this.trigger('onChangeModel', {oldModelInfo, model});
  }

  _onModelRemove(model) {
    this.trigger('onRemoveModel', model);
  }

  _onInitMode() { // 시뮬레이터 초기 모드 실행
    this.trigger('onInitMode');
  }

  _onViewMode() { // 시뮬레이터 뷰 모드 실행
    this.trigger('onViewMode');
  }

  _onEditMode(model) { // 시뮬레이터 수정 모드 실행
    this.trigger('onEditMode', model);
  }

  _onModelLoadingStart() {
    this.trigger('onLoadingModelStart');
  }

  _onModelLoadingProgress() {
    this.trigger('onLoadingModelProgress');
  }

  _onModelLoadingEnd() {
    this.trigger('onLoadingModelEnd');
  }

  // Action

  setViewMode() {
    if(this._mode !== 'view') {
      this.hideMessage();
    }
  }

  setSingleMode(enabled) {
    this._config.singleMode = enabled;
  }

  getCurrentType() {
    if (this._current_model) {
      return this._current_model.getType();
    }

    return false;
  }

  getTable() {
    return this._table;
  }

  getChair(index) {
    return this._table ? this._table.getChair(index) : false;
  }

  getChairs() {
    return this._table ? this._table.getChairs() : false;
  }

  // 테이블 추가 (만약 기존 테이블이 있다면 교체)
  addTable(param) {
    this._resetViewSetting();

    if (this._config.singleMode) {
      if (this._current_model) {
        this._current_model.delete();
        this._current_model = null;
      }
    }

    if (this._table) {
      // 이미 테이블이 있음
      if (this._edit_model) {
        // 편집 모드 중
      }

      this._table.loadModel(param, true);
    } else {
      // 테이블이 없음
      let chair = null;
      if (this._current_model) {
        // 이미 체어가 있다면 테이블 추가 후 해당 테이블 기본 추가
        if (this._current_model.getType() === "chair") {
          this.removeFromScene(this._current_model.getModel());
          chair = this._current_model;
          this._current_model = null;
        }
      }

      this._table = new Table(this, {
        viewChairBound: false,
      });

      this._table.on("delete", () => {
        this._reset();
      });

      this._current_model = this._table;

      this._table.on("loadModel", () => {
        this._linear_dimension.setTarget(this._table);
        this._linear_dimension.create();

        if (chair) {
          this._table.addChair(chair);
          chair.setParent(this._table);

          chair = null;
        }

        this._table.zoom(this._config.view.zoomPosition.zoom, new THREE.Vector3(this._config.view.zoomPosition.x, this._config.view.zoomPosition.y, this._config.view.zoomPosition.z), false);
      });

      this._table.loadModel(param);
    }

    this._check_page();
  }

  // 체어 추가 (만약에 테이블이 있다면 조합 모드 없다면 교체 모드)
  addChair(param) {
    this._resetViewSetting();

    if (this._config.singleMode) {
      if (this._current_model) {
        this._current_model.delete();
        this._current_model = null;
      }
    }

    if (this._edit_model) {
      // 편집 모드 중
      this._edit_model.change(param);
    } else {
      if (this._current_model) {
        if (this._current_model.getType() === "table") {
          // 테이블이 이미 있다면 추가 모드
          let chair = new Chair(this._current_model, param, this);
          this._current_model.addChair(chair);
        } else if (this._current_model.getType() === "chair") {
          // 이미 체어가 있다면 바꿈 모드
          this._current_model.delete();

          let chair = new Chair(null, param, this);
          this._current_model = chair;

          chair.setSimulator(this);

          chair.on("loadModel", () => {
            if (this._table) {
            } else {
              chair.addToScene(this._scene);

              this._linear_dimension.setTarget(chair);
              this._linear_dimension.create();
              chair.zoom(this._config.view.zoomPosition.zoom, new THREE.Vector3(this._config.view.zoomPosition.x, this._config.view.zoomPosition.y, this._config.view.zoomPosition.z));
            }
          });
        }
      } else {
        // 아무것도 없다면 추가 바꿈 모드
        let chair = new Chair(null, param, this);
        this._current_model = chair;

        chair.setSimulator(this);

        chair.on("loadModel", () => {
          if (this._table) {
          } else {
            chair.addToScene(this._scene);

            this._linear_dimension.setTarget(chair);
            this._linear_dimension.create();
            chair.zoom(this._config.view.zoomPosition.zoom, new THREE.Vector3(this._config.view.zoomPosition.x, this._config.view.zoomPosition.y, this._config.view.zoomPosition.z), false);
          }
        });
      }
    }

    this._check_page();
  }

  deleteTable() { // 테이블을 삭제하는 것은 모두 삭제하는 것과 같도록
    this.deleteAll()
  }

  deleteChair(index) {
    const chair = this.getChair(index);
    // console.log(index, chair,this._table._chairs);
    if (chair) this._table.removeChair(chair);

    this._check_page();
  }

  deleteAll() {
    this._resetViewSetting();
    this._linear_dimension.setTarget(null);

    if (this._current_model) {
      if (this._current_model.getType() === "chair") {
        this._current_model.delete();
        this._current_model = null;
      } else if (this._current_model.getType() === "table") {
        this._current_model.removeAllChair();
        this._current_model.delete();
        this._current_model = null;
      }
    }

    if (this._table) {
      this._table = null;
    }

    this._check_page();
  }

  removeFromScene(model) {
    this._scene.remove(model);

    this._check_page();
  }

  viewZoom() {
    if (this._current_model) {
      this._resetViewSetting();

      this._current_model.zoom(this._config.view.zoomPosition.zoom, new THREE.Vector3(this._config.view.zoomPosition.x, this._config.view.zoomPosition.y, this._config.view.zoomPosition.z));
    }
  }

  viewDimension() {
    this._resetViewSetting();

    if (this._current_model) {
      this._current_model.zoom(this._config.view.dimensionPosition.zoom, new THREE.Vector3(this._config.view.dimensionPosition.x, this._config.view.dimensionPosition.y, this._config.view.dimensionPosition.z));
    }

    this._update("viewDimension", true);
  }

  viewPopup(popupData) {
    this.hidePopup();

    this._popup = {};
    this._popup.html = document.createElement("div");
    this._popup.html.classList.add("simulator-popup");

    var popupMenus = this._makePopup(popupData);
    if (popupMenus) {
      this._popup.html.append(popupMenus);
    }

    this._wrapper.append(this._popup.html);

    if (popupData.position) {
      var popupPosition = popupData.position.clone();

      var popupWidth = this._popup.html.offsetWidth;
      var popupHeight = this._popup.html.offsetHeight;

      this._popup.html.style.left = `${popupPosition.x - popupWidth / 2}px`;
      this._popup.html.style.top = `${popupPosition.y - popupHeight / 2}px`;
    }
  }

  hidePopup() {
    if (this._popup) {
      this._popup.html.remove();
      this._popup = null;
    }
  }

  takeScreenshot(download = false) {
    if (this._current_model) this._current_model.zoom(1);

    this._renderer.render(this._scene, this._camera);
    const src = this._renderer.domElement.toDataURL();

    if (download) {
      const fileName = "capture.png";
      if (typeof window.navigator.msSaveBlob !== "undefined") {
        window.navigator.msSaveBlob(
          src.replace("image/png", "image/octet-stream"),
          fileName
        );
      } else {
        var link = document.createElement("a");
        link.href = src.replace("image/png", "image/octet-stream");
        link.download = fileName;
        link.click();
      }
    } else {
      return src;
    }
  }

  _makePopup(popupData) {
    var self = this;
    var menus = null;

    if (popupData && popupData.menus) {
      menus = document.createElement("ul");

      for (const [key, value] of Object.entries(popupData.menus)) {
        var menuItem = document.createElement("li");
        var menuClick = document.createElement("a");
        menuClick.innerHTML = value.title;
        menuClick.className = value.class;
        menuClick.onclick = function () {
          value.event();

          self.hidePopup();

          return false;
        };

        menuItem.appendChild(menuClick);
        menus.appendChild(menuItem);
      }
    }

    return menus;
  }

  _update(key, value) {
    switch (key) {
      case "viewDimension":
        this._config.view.dimension = value;
        if (!this._config.view.dimension) {
          this._linear_dimension.hide();
          if(this._table) this._table.showChairs();
        } else {
          this._linear_dimension.show();
          this._linear_dimension.update(this._camera);
          if(this._table) this._table.hideChairs();
        }
        break;
    }
  }

  _render(time) {
    if(this._config.viewStats) this._stats.begin();

    const elapsedTime = this._clock.getElapsedTime();
    this._renderer.render(this._scene, this._camera);

    if(this._config.viewStats) this._stats.end();

    requestAnimationFrame(this._render.bind(this));

    this._controls.update();
  }

  _getCurrentMousePosition(e) {
    const parentSize = this._wrapper.getBoundingClientRect();
    
    const isTouchDevice = "ontouchstart" in document.documentElement;
    const clientX = isTouchDevice ? e.changedTouches[0].clientX : e.clientX;
    const clientY = isTouchDevice ? e.changedTouches[0].clientY : e.clientY;
    return { x: clientX - parentSize.x , y: clientY - parentSize.y};
  }

  _click(e) {
    if (
      e.target &&
      e.target.tagName === "CANVAS" &&
      e.target.className === "simulator-canvas"
    ) {
      e.preventDefault();

      // 열려있는 팝업 닫기
      this.hidePopup();

      // if (this._table) 
      {
        var mouse = new THREE.Vector2();

        const clientPos = this._getCurrentMousePosition(e);

        mouse.x = (clientPos.x / this._renderer.domElement.clientWidth) * 2 - 1;
        mouse.y =
          -(clientPos.y / this._renderer.domElement.clientHeight) * 2 + 1;

        this._raycaster.setFromCamera(mouse, this._camera);

        var intersects = this._raycaster.intersectObjects(this._scene.children);

        if (intersects.length > 0) {
          var clickModel = null;
          if(this._table) {
            clickModel = this._table._getParentModel(
              intersects[0].object
            );
          } else if(this._current_model) {
            clickModel = this._current_model;
          }

          if (clickModel) {
            clickModel.click();
          }
        }
      }
    }
  }

  _resize() {
    const parentSize = this._wrapper.getBoundingClientRect();

    this._sizes.width = parentSize.width;
    this._sizes.height = parentSize.height;

    this._camera.aspect = this._sizes.width / this._sizes.height;
    this._camera.updateProjectionMatrix();
    
    this._renderer.setSize(this._sizes.width, this._sizes.height);
    
    if (this._config.view.dimension) {
      this.viewDimension();
      // this._linear_dimension.update(this._camera);
    }

  }
}

export default Simulator;
